import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, Download, CheckCircle2,
  AlertTriangle, XCircle, Loader2, Zap, ChevronDown, ChevronUp,
  FileWarning, Hash, Copy, Package, Tag
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Map Fields", "Audit & Import"];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-1" data-testid="items-step-indicator">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            i < current ? "bg-[#10b981]/10 text-[#10b981]" : i === current ? "bg-[#4338ca] text-white" : "bg-[#f1f5f9] text-[#94a3b8]"
          }`} data-testid={`items-step-${i}`}>
            {i < current ? <CheckCircle2 className="h-3 w-3" /> : <span className="w-4 text-center">{i + 1}</span>}
            <span className="hidden sm:inline">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`w-6 h-px mx-1 ${i < current ? "bg-[#10b981]" : "bg-[#e2e8f0]"}`} />}
        </div>
      ))}
    </div>
  );
}

function UploadStep({ onParsed }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/import-items/sample-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "bizflowa_items_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Template downloaded!");
    } catch { toast.error("Failed to download template"); }
  };

  const processFile = async (file) => {
    if (!file || !file.name.endsWith(".csv")) { toast.error("Please upload a .csv file"); return; }
    setLoading(true);
    try {
      const text = await file.text();
      const r = await api.post("/import-items/parse-csv", { csv_content: text });
      onParsed({ ...r.data, csv_content: text });
      toast.success(`Parsed ${r.data.total_rows} items`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to parse CSV"); }
    finally { setLoading(false); }
  };

  const onDrop = useCallback((e) => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer?.files?.[0]); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0f172a]">Upload Items/Services</h2>
          <p className="text-sm text-[#64748b] mt-0.5">Import your product & service catalog from CSV</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-items-template-btn"
          className="border-[#4338ca]/20 text-[#4338ca] hover:bg-[#4338ca]/5 rounded-lg">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Download Sample Template
        </Button>
      </div>
      <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
        data-testid="items-csv-drop-zone"
        className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${dragging ? "border-[#4338ca] bg-[#4338ca]/5" : "border-[#e2e8f0] hover:border-[#cbd5e1] bg-[#f8fafc]"}`}>
        <input type="file" accept=".csv" onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ""; }} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="items-csv-file-input" />
        {loading ? (
          <div className="flex flex-col items-center gap-3"><Loader2 className="h-10 w-10 text-[#4338ca] animate-spin" /><p className="text-sm text-[#64748b]">Reading CSV...</p></div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-[#4338ca]/10 flex items-center justify-center"><Upload className="h-6 w-6 text-[#4338ca]" /></div>
            <div><p className="text-sm font-medium text-[#0f172a]">Drop your CSV here, or click to browse</p><p className="text-xs text-[#94a3b8] mt-1">Supports .csv with item/service data</p></div>
          </div>
        )}
      </div>
      <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4">
        <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {["Name", "Type (goods/service)", "HSN/SAC", "Unit", "Rate", "GST Rate", "Description"].map((c) => (
            <span key={c} className="px-2 py-1 bg-white border border-[#e2e8f0] rounded-md text-[11px] text-[#64748b] font-mono">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewStep({ data }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-semibold text-[#0f172a]">Data Preview</h2><p className="text-sm text-[#64748b]">Showing first {data.preview.length} of {data.total_rows} rows</p></div>
        <Badge className="bg-[#4338ca]/10 text-[#4338ca]"><FileSpreadsheet className="h-3 w-3 mr-1" /> {data.total_rows} items detected</Badge>
      </div>
      <div className="overflow-x-auto border border-[#e2e8f0] rounded-xl">
        <table className="w-full text-xs" data-testid="items-preview-table">
          <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-10">#</th>
            {data.headers.map((h, i) => (<th key={i} className="py-2.5 px-3 text-left text-[#64748b] font-medium whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>{data.preview.map((row, rIdx) => (
            <tr key={rIdx} className="border-b border-[#f1f5f9] last:border-0" data-testid={`items-preview-row-${rIdx}`}>
              <td className="py-2 px-3 text-[#94a3b8] font-mono">{rIdx + 1}</td>
              {row.map((cell, cIdx) => (<td key={cIdx} className="py-2 px-3 text-[#0f172a] whitespace-nowrap max-w-[200px] truncate">{cell || <span className="text-[#cbd5e1]">—</span>}</td>))}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function MappingStep({ data, mapping, setMapping }) {
  const { headers, bizflowa_fields } = data;
  return (
    <div className="space-y-4">
      <div><h2 className="text-lg font-semibold text-[#0f172a]">Map Your Columns</h2><p className="text-sm text-[#64748b]">Match CSV columns to BizFlowa item fields</p></div>
      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden" data-testid="items-mapping-table">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">BizFlowa Field</th>
            <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">Your CSV Column</th>
          </tr></thead>
          <tbody>{bizflowa_fields.map((bf) => {
            const isMatched = !!mapping[bf];
            return (
              <tr key={bf} className="border-b border-[#f1f5f9] last:border-0">
                <td className="py-2.5 px-4"><div className="flex items-center gap-2">
                  {isMatched ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> : <div className="h-3.5 w-3.5 rounded-full border-2 border-[#cbd5e1]" />}
                  <span className={`text-sm font-medium ${isMatched ? "text-[#0f172a]" : "text-[#94a3b8]"}`}>{bf}</span>
                  {bf === "Name" && <span className="text-[9px] text-red-400 font-bold">REQ</span>}
                </div></td>
                <td className="py-2.5 px-4">
                  <Select value={mapping[bf] || "_none"} onValueChange={(v) => { const n = { ...mapping }; if (v === "_none") delete n[bf]; else n[bf] = v; setMapping(n); }}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`items-mapping-${bf.replace(/[\s/]/g, "-").toLowerCase()}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none"><span className="text-[#94a3b8]">— Not mapped —</span></SelectItem>
                      {headers.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
}

function AuditStep({ auditResult, onCommitValid, onRetry, committing }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showFilter, setShowFilter] = useState("all");
  if (!auditResult) return null;

  const { total_rows, valid_count, invalid_count, error_summary, rows } = auditResult;
  const toggleRow = (n) => { const s = new Set(expandedRows); s.has(n) ? s.delete(n) : s.add(n); setExpandedRows(s); };

  const filtered = rows.filter((r) => {
    if (showFilter === "errors") return !r.valid;
    if (showFilter === "warnings") return r.warnings.length > 0 && r.valid;
    if (showFilter === "valid") return r.valid && r.warnings.length === 0;
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="items-audit-summary">
        <Card className="border-[#e2e8f0]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#0f172a]">{total_rows}</p><p className="text-[11px] text-[#64748b] mt-0.5">Total Items</p></CardContent></Card>
        <Card className="border-[#10b981]/20 bg-[#10b981]/5"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#10b981]">{valid_count}</p><p className="text-[11px] text-[#10b981] mt-0.5">Ready to Import</p></CardContent></Card>
        <Card className="border-red-200 bg-red-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{invalid_count}</p><p className="text-[11px] text-red-400 mt-0.5">Has Errors</p></CardContent></Card>
        <Card className="border-[#f59e0b]/20 bg-[#f59e0b]/5"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#f59e0b]">{rows.filter(r => r.warnings.length > 0).length}</p><p className="text-[11px] text-[#f59e0b] mt-0.5">Warnings</p></CardContent></Card>
      </div>

      {(invalid_count > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2" data-testid="items-error-breakdown">
          <p className="text-sm font-semibold text-red-700">Issues Found</p>
          <div className="flex flex-wrap gap-2">
            {error_summary.hsn > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><Hash className="h-3 w-3" /> {error_summary.hsn} invalid HSN codes</Badge>}
            {error_summary.gst_rate > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><Tag className="h-3 w-3" /> {error_summary.gst_rate} invalid GST rates</Badge>}
            {error_summary.type_invalid > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><Package className="h-3 w-3" /> {error_summary.type_invalid} invalid types</Badge>}
            {error_summary.missing_fields > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><FileWarning className="h-3 w-3" /> {error_summary.missing_fields} missing fields</Badge>}
          </div>
        </div>
      )}

      {rows.some(r => r.warnings.some(w => w.type === "duplicate")) && (
        <div className="bg-[#f59e0b]/5 border border-[#f59e0b]/20 rounded-xl p-4 space-y-1" data-testid="items-duplicate-warning">
          <div className="flex items-center gap-2"><Copy className="h-4 w-4 text-[#f59e0b]" /><p className="text-sm font-semibold text-[#f59e0b]">Duplicate Items Detected</p></div>
          <p className="text-xs text-[#64748b]">Some items already exist in your catalog. They will be imported as separate entries.</p>
        </div>
      )}

      <div className="flex items-center gap-2">
        {[
          { key: "all", label: `All (${total_rows})` },
          { key: "errors", label: `Errors (${invalid_count})` },
          { key: "warnings", label: `Warnings (${rows.filter(r => r.warnings.length > 0).length})` },
          { key: "valid", label: `Clean (${rows.filter(r => r.valid && r.warnings.length === 0).length})` },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setShowFilter(tab.key)} data-testid={`items-filter-${tab.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${showFilter === tab.key ? "bg-[#4338ca] text-white" : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"}`}>{tab.label}</button>
        ))}
      </div>

      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden max-h-[340px] overflow-y-auto" data-testid="items-audit-rows">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10"><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-12">Row</th>
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium">Name</th>
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-16">Type</th>
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-20">HSN/SAC</th>
            <th className="py-2.5 px-3 text-right text-[#64748b] font-medium w-20">Rate</th>
            <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-16">GST%</th>
            <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-20">Status</th>
            <th className="py-2.5 px-3 w-8" />
          </tr></thead>
          <tbody>
            {filtered.map((r) => {
              const hasIssues = r.errors.length > 0 || r.warnings.length > 0;
              return (
                <tr key={r.row} className="border-b border-[#f1f5f9] last:border-0" data-testid={`items-audit-row-${r.row}`}>
                  <td className="py-2 px-3 font-mono text-[#94a3b8] align-top">{r.row}</td>
                  <td className="py-2 px-3 text-[#0f172a] font-medium align-top">{r.data.name || "—"}</td>
                  <td className="py-2 px-3 align-top">
                    <Badge className={r.data.type === "goods" ? "bg-[#4338ca]/10 text-[#4338ca] text-[10px]" : "bg-[#10b981]/10 text-[#10b981] text-[10px]"}>
                      {r.data.type === "goods" ? "Goods" : "Service"}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 font-mono text-[#64748b] align-top">{r.data.hsn_sac || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#0f172a] align-top">{formatINR(r.data.rate)}</td>
                  <td className="py-2 px-3 text-center text-[#64748b] align-top">{r.data.gst_rate}%</td>
                  <td className="py-2 px-3 text-center align-top">
                    {r.valid ? (r.warnings.length > 0 ? <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Warn</Badge> : <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]">OK</Badge>) : <Badge className="bg-red-100 text-red-500 text-[10px]">Error</Badge>}
                  </td>
                  <td className="py-2 px-3 align-top">{hasIssues && <button onClick={() => toggleRow(r.row)} className="text-[#94a3b8] hover:text-[#64748b]">{expandedRows.has(r.row) ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>}</td>
                </tr>
              );
            })}
            {filtered.map((r) => expandedRows.has(r.row) ? (
              <tr key={`${r.row}-d`} className="bg-[#fefce8]"><td colSpan={8} className="px-6 py-2"><div className="space-y-1">
                {r.errors.map((e, i) => <div key={i} className="flex items-center gap-2 text-xs text-red-600"><XCircle className="h-3 w-3 shrink-0" /> {e.message}</div>)}
                {r.warnings.map((w, i) => <div key={i} className="flex items-center gap-2 text-xs text-[#f59e0b]"><AlertTriangle className="h-3 w-3 shrink-0" /> {w.message}</div>)}
              </div></td></tr>
            ) : null)}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-2" data-testid="items-audit-actions">
        <Button variant="outline" onClick={onRetry} className="rounded-xl" data-testid="items-fix-retry-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Fix Errors & Retry</Button>
        <Button onClick={onCommitValid} disabled={committing || valid_count === 0} data-testid="items-import-valid-btn" className="bg-[#10b981] hover:bg-[#059669] rounded-xl shadow-sm">
          {committing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {committing ? "Importing..." : `Import ${valid_count} Valid Item${valid_count !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}

export default function ItemsImporterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [csvData, setCsvData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const handleParsed = (data) => { setCsvData(data); setMapping(data.suggested_mapping || {}); setStep(1); };

  const runAudit = async () => {
    if (!csvData) return;
    setAuditing(true);
    try {
      const r = await api.post("/import-items/audit", { csv_content: csvData.csv_content, mapping });
      setAuditResult(r.data); setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || "Audit failed"); }
    finally { setAuditing(false); }
  };

  const commitValid = async () => {
    if (!auditResult) return;
    setCommitting(true);
    try {
      const validRows = auditResult.rows.filter(r => r.valid).map(r => r.data);
      const r = await api.post("/import-items/commit", { rows: validRows });
      toast.success(`Imported ${r.data.imported_count} items!`);
      setTimeout(() => navigate("/items"), 1200);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setCommitting(false); }
  };

  const handleRetry = () => { setAuditResult(null); setStep(0); setCsvData(null); setMapping({}); };
  const canProceed = useMemo(() => { if (step === 1) return true; if (step === 2) return Object.keys(mapping).length >= 1; return false; }, [step, mapping]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto" data-testid="items-importer-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/items")} className="text-[#64748b] hover:text-[#0f172a]" data-testid="back-to-items-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Items & Services Importer</h1>
            <p className="text-sm text-[#64748b] mt-0.5">Import your product catalog with HSN/SAC & GST validation</p>
          </div>
        </div>
      </div>
      <StepIndicator current={step} />
      <Card className="bg-white border-[#e2e8f0] shadow-sm"><CardContent className="p-6">
        {step === 0 && <UploadStep onParsed={handleParsed} />}
        {step === 1 && csvData && <PreviewStep data={csvData} />}
        {step === 2 && csvData && <MappingStep data={csvData} mapping={mapping} setMapping={setMapping} />}
        {step === 3 && <AuditStep auditResult={auditResult} onCommitValid={commitValid} onRetry={handleRetry} committing={committing} />}
      </CardContent></Card>
      {step > 0 && step < 3 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-xl" data-testid="items-prev-step-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          {step === 2 ? (
            <Button onClick={runAudit} disabled={auditing || !canProceed} data-testid="items-map-scan-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}{auditing ? "Scanning..." : "Map & Scan"}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed} data-testid="items-next-step-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">Next <ArrowRight className="h-4 w-4 ml-2" /></Button>
          )}
        </div>
      )}
    </div>
  );
}
